import type React from 'react';
import { useEffect, useState } from 'react';
import type { RefCallBack } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CirclePlus, SearchIcon } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';
import type { Estate } from '@/features/estates/api/estates';
import { getEstates } from '@/features/estates/api/estates';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import type { Projects } from '@/features/projects/api/projects';
import { getProjectsQueryOptions } from '@/features/projects/api/projects';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import { getFinalLivingArea } from '@/utils/getFinalLivingArea';
import { getFinalPrice } from '@/utils/getFinalPrice';
import type { AtLeastOne } from '@/utils/types/helpers';
import { useModal } from '../../../components/modals/new/use-modal';
import { CreateEstateModal } from '../../../components/modals/create-estate-modal';

type Project = Projects[number];

interface ObjectRelationSettingsContentProps {
  closePopover: () => void;
  setSelectedObject: (object: Estate | Project) => void;
  onChangeEstateId: React.Dispatch<React.SetStateAction<string | undefined>>;
  onChangeProjectId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const ObjectRelationSettingsContent = ({
  closePopover,
  onChangeEstateId,
  setSelectedObject,
  onChangeProjectId,
}: ObjectRelationSettingsContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModal();

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [queriedObjects, setQueriedObjects] = useState<Array<Estate | Project>>([]);

  const {
    data: projects,
    isLoading,
    refetch,
  } = useQuery(
    getProjectsQueryOptions({
      query: {
        page: 1,
        pageSize: 999,
      },
    })
  );

  const { register, watch } = useForm<{ searchString: string }>({
    defaultValues: { searchString: '' },
  });

  const searchString = watch('searchString');

  const getDebouncedEstates = useDebouncedCallback(async () => {
    try {
      setLoading(true);
      const estates = await getEstates({
        query: {
          ...(searchString
            ? {
                filters: {
                  searchString,
                },
              }
            : {}),
        },
      });

      const filteredProjects = (projects || []).filter((project) =>
        (project.name || '').toLowerCase().includes(searchString.toLowerCase())
      );

      setQueriedObjects([...estates, ...filteredProjects]);
    } finally {
      setLoading(false);
    }
  });

  const { ref, ...restInputProps } = register('searchString');

  const inputRefCallbackHandler: RefCallBack = (e) => {
    ref(e);
    setInputRef(e);
  };

  const onAddObject = () => {
    modal.open();
  };

  const handleChangeObject = (object: Estate | Project | string) => {
    if (typeof object === 'string') {
      onChangeEstateId(object);
    } else if ('estateDetails' in object) {
      onChangeProjectId(undefined);
      onChangeEstateId(object.id);
      setSelectedObject(object);
    } else {
      onChangeEstateId(undefined);
      onChangeProjectId(object.id);
      setSelectedObject(object);
    }
    closePopover();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore
  useEffect(() => {
    getDebouncedEstates();
  }, [searchString]);

  useEffect(() => {
    if (inputRef) inputRef.focus();
  }, [inputRef]);

  return (
    <>
      <div className="w-full">
        <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 sm:h-[60px]">
          <span className="text-sm font-semibold text-white">{t('object')}</span>
        </div>
        <div className="bg-white p-2 sm:px-5 sm:py-6">
          <div className="flex w-full max-w-screen-sm items-center gap-2">
            <Input
              {...restInputProps}
              ref={inputRefCallbackHandler}
              size="sm"
              placeholder={t('search')}
              className="border"
              startIcon={<SearchIcon className="text-primary-light" />}
            />
            <CirclePlus
              onClick={onAddObject}
              className="mx-auto size-10 cursor-pointer text-primary-light sm:mx-0"
            />
          </div>
          <div className="relative mt-3 flex h-screen max-h-calc-full-minus-100 items-center justify-center overflow-y-auto overflow-x-visible sm:max-h-96">
            {loading || isLoading ? (
              <div className="absolute z-10 flex h-screen max-h-calc-full-minus-100 flex-col-reverse items-center justify-center bg-background sm:max-h-96">
                <Spinner className="size-12 [&_*]:stroke-primary-dark" />
              </div>
            ) : queriedObjects.length > 0 ? (
              <div className="flex h-full w-full flex-col gap-3">
                {queriedObjects.map((object) => {
                  const price = getFinalPrice(object);
                  const livingArea = getFinalLivingArea(object);

                  return (
                    <Card
                      key={`estate-card${object.id}`}
                      className="grid h-[105px] min-h-[105px] cursor-pointer grid-cols-[140px,auto] rounded-none"
                      onClick={() => handleChangeObject(object)}
                    >
                      <div className="flex size-full overflow-hidden">
                        <EstateImage
                          className="size-full"
                          {...(object.thumbnail?.mediaId && {
                            thumbnailId: object.thumbnail?.mediaId,
                          })}
                        />
                      </div>
                      <div className="flex flex-1 flex-col pl-3">
                        <span className="max-w-[270px] truncate text-sm max-md:mb-2 max-sm:w-48">
                          {object.name}
                        </span>
                        <span className="block max-w-[270px] truncate text-[10px] font-medium">
                          {`${getRenderValue(object.address?.city)}, ${getRenderValue(object.address?.address?.split(', ')?.[1])}, ${getRenderValue(object.address?.postalCode)}, ${getRenderValue(object.address?.city)}`}
                        </span>
                        <div className="mb-1 mt-auto grid grid-cols-3 gap-3">
                          <div>
                            <span className="truncate text-[10px] font-medium leading-[28px] text-[#A0A0A0]">
                              {t('area')}
                            </span>
                            <span className="block truncate text-[10px] font-medium leading-tight">
                              {getRenderValue(livingArea)} m<sup>2</sup>
                            </span>
                          </div>
                          <div>
                            <span className="truncate text-[10px] font-medium leading-[28px] text-[#A0A0A0]">
                              {t('price')}
                            </span>
                            <span className="block truncate whitespace-break-spaces text-[10px] font-medium leading-tight">
                              {getRenderValue(price)}
                            </span>
                          </div>
                          <div>
                            <span className="truncate text-[10px] font-medium leading-[28px] text-[#A0A0A0]">
                              {t('author')}
                            </span>
                            <span className="block truncate whitespace-break-spaces text-[10px] font-medium leading-tight">
                              {getRenderValue(
                                `${getRenderValue(object.creator?.firstName)} ${getRenderValue(object.creator?.lastName)}`
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <span>{t('noObjects')}</span>
            )}
          </div>
        </div>
      </div>
      <CreateEstateModal
        modal={modal}
        navigate={navigate}
        onSuccess={(object: AtLeastOne<{ estateId?: string; projectId?: string }>) => {
          if (object?.estateId) {
            getDebouncedEstates();
            handleChangeObject(object.estateId);
          }
          if (object?.projectId) {
            refetch();
            handleChangeObject(object.projectId);
          }
        }}
      />
    </>
  );
};
